import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { CSSTransition } from 'react-transition-group';

import Tippy from '@tippyjs/react';
import { VscInfo } from 'react-icons/vsc';

import { FaChevronDown } from 'react-icons/fa';

import { openDrawer } from 'features/drawersSlice';

import { Channel } from 'components/channel';
import { DesktopHorizontalTooltipContent } from './DesktopHorizontalTooltipContent';
import { HorizontalWidgetPriceBlock } from './HorizontalWidgetPriceBlock';
import { OldWidgetLoadingIndicator } from 'components/common/oldWidgetLoadingIndicator';
import { ToggleWidgetRoundBtn } from 'components/common/toggleWidgetRoundBtn';

import { WIDGET_STATS_SETTINGS } from 'config';
import { CLICK_ACTIONS } from './../../constants';
import {
    applyImportantStyles,
    getWidgetPosition,
    isValidPrice,
    makeStatsRequestThrottled,
} from 'lib';
import { useOpenOldWidget } from 'hooks';

import './DesktopHorizontalBookingWidget.css';

const DesktopHorizontalBookingWidget = ({ showWidget, toggleWidget }) => {
    const dispatch = useDispatch();

    const isOpen = useSelector((state) => state.miniBookingWidget.open);
    const hidden = useSelector((state) => state.miniBookingWidget.hidden);
    const data = useSelector((state) => state.miniBookingWidget?.data) || {};

    // This was used in order for the animation to play correctly
    const [showToggleBtn, setShowToggleBtn] = useState(!isOpen);
    // Show cta and animate toggle button
    const [showCta, setShowCta] = useState(true);
    const nodeRef = useRef(null);

    const {
        Behavior: clickAction,
        colors,
        enableXButton: enableXBtn,
        Lang,
        poweredBy,
        propertyID: propertyId,
        ourPrice,
        toggleWidgetRoundBtn,
        noPricesMessage,
    } = data;

    const channels =
        useSelector((state) => state.miniBookingWidget?.channels) || {};
    const show = useSelector((state) => state.miniBookingWidget.show);
    const widgetPosition = useSelector(
        (state) => state.miniBookingWidget.widgetPosition
    );

    const widgetPositionStyles = getWidgetPosition(widgetPosition);
    const widgetXPosition = widgetPosition?.position?.split('-')[1] || 'right';

    const desktopHorizontalWidgetStyles = {
        ...widgetPositionStyles,
        display: hidden ? 'none' : 'block',
    };
    const desktopHorizontalWidgetCntStyles = {
        boxShadow: `3px 3px 0px 0px ${toggleWidgetRoundBtn.backgroundColor} inset, -3px -3px 0px 0px ${toggleWidgetRoundBtn.backgroundColor} inset`,
        position: 'relative',
    };
    const closeBtnStyles = {
        backgroundColor: toggleWidgetRoundBtn.backgroundColor,
        color: toggleWidgetRoundBtn.color,
    };

    const { showLoader: showOldWidgetLoader, clickHandler: openOldWidget } =
        useOpenOldWidget();

    const handleClick = () => {
        if (clickAction === CLICK_ACTIONS.OPEN_BIG_PLUGIN) {
            dispatch(openDrawer({ drawerId: 'BigBookingWidgetDrawer' }));
        } else {
            openOldWidget(clickAction);
        }
        makeStatsRequestThrottled(
            WIDGET_STATS_SETTINGS.actions.click,
            propertyId
        );
    };

    const chatBtnHandleClick = useCallback(() => {
        openOldWidget(CLICK_ACTIONS.OPEN_CHAT);
    }, []);

    const cursorPointerStyles = {
        cursor:
            clickAction === CLICK_ACTIONS.OPEN_PLUGIN ||
            clickAction === CLICK_ACTIONS.OPEN_BIG_PLUGIN ||
            clickAction === CLICK_ACTIONS.LINK
                ? 'pointer'
                : 'default',
    };

    // TODO: MAKE THIS BETTER
    const useLoyaltyPrice = isValidPrice(ourPrice?.loyaltyprice);

    const bestPriceObj = {
        bookingRefText: data?.bookingRefText,
        clickAction,
        currencySymbol: ourPrice?.currencySymbol,
        price: useLoyaltyPrice ? ourPrice?.loyaltyprice : ourPrice?.price,
        priceText: Lang?.officialBestRate,
        url: useLoyaltyPrice ? ourPrice.loyaltyurl : ourPrice.url,
    };

    useEffect(() => {
        // We want to show the 'CTA' only the first time
        // that is why we disable it
        setShowCta(false);
    }, []);

    const inlineStyles = {};
    if (data.pluginZIndex) {
        inlineStyles.zIndex = data.pluginZIndex;
    }

    if (!show) {
        return null;
    }

    return (
        <div
            className="ab-desktop-horizontal-widget"
            style={inlineStyles}
            ref={(el) => {
                applyImportantStyles(el, desktopHorizontalWidgetStyles);
            }}
        >
            <CSSTransition
                nodeRef={nodeRef}
                in={isOpen}
                timeout={300}
                classNames="ab-desktop-horizontal-widget-animate"
                unmountOnExit
                onEnter={() => setShowToggleBtn(false)}
                onExited={() => setShowToggleBtn(true)}
                appear={true}
            >
                <div className="ab-booking-widget-box-shadow" ref={nodeRef}>
                    <div
                        className={`ab-desktop-horizontal-widget-cnt ab-desktop-horizontal-widget-cnt--${widgetXPosition}`}
                        style={desktopHorizontalWidgetCntStyles}
                    >
                        <div className="ab-desktop-horizontal-widget-close-btn-cnt">
                            <button
                                onClick={toggleWidget}
                                style={closeBtnStyles}
                            >
                                <FaChevronDown size={24} />
                            </button>
                        </div>
                        <div
                            className={`ab-desktop-horizontal-widget-our-price ab-desktop-horizontal-widget-our-price--${widgetXPosition}`}
                            onClick={handleClick}
                            style={cursorPointerStyles}
                        >
                            <div
                                className={`ab-desktop-horizontal-widget-price ab-desktop-horizontal-widget-price--${widgetXPosition}`}
                                style={{
                                    color: colors.horizontalWidgetPrimaryColor,
                                }}
                            >
                                <HorizontalWidgetPriceBlock
                                    {...bestPriceObj}
                                    noPricesMsg={noPricesMessage}
                                    color={colors.horizontalWidgetPrimaryColor}
                                />
                            </div>
                            {Lang?.OurBestPrice && (
                                <div
                                    className={`ab-desktop-horizontal-widget-text ab-desktop-horizontal-widget-text--${widgetXPosition}`}
                                    dangerouslySetInnerHTML={{
                                        __html: Lang?.OurBestPrice,
                                    }}
                                ></div>
                            )}
                        </div>
                        <div
                            className="ab-desktop-horizontal-widget-channels"
                            onClick={handleClick}
                            style={cursorPointerStyles}
                        >
                            {channels.map((channel, index) => {
                                return (
                                    <>
                                        {channel?.price && (
                                            <div
                                                className="ab-desktop-horizontal-widget-channel-cnt"
                                                key={`desktop-horizontal-widghet-channel-cnt--${index}`}
                                            >
                                                <Channel
                                                    // url={channel?.url}
                                                    // logo={channel?.logo}
                                                    name={channel?.name}
                                                    currencySymbol={
                                                        channel?.currencySymbol
                                                    }
                                                    price={channel?.price}
                                                    extraClasses={[
                                                        'ab-channel--desktop-horizontal',
                                                    ]}
                                                    channelId={
                                                        channel?.channelID
                                                    }
                                                />
                                            </div>
                                        )}
                                    </>
                                );
                            })}
                        </div>

                        <div
                            className={`ab-desktop-horizontal-widget-info-icon-cnt ab-desktop-horizontal-widget-info-icon-cnt--${widgetXPosition}`}
                            onClick={handleClick}
                        >
                            <Tippy
                                content={
                                    <DesktopHorizontalTooltipContent
                                        content={Lang?.WeDoResearch}
                                        poweredBy={poweredBy}
                                    />
                                }
                                arrow={false}
                                position={0}
                                maxWidth={250}
                            >
                                <span>
                                    <VscInfo
                                        style={{
                                            lineHeight: 'inherit',
                                            color: 'inherit',
                                        }}
                                        size={17}
                                    />
                                </span>
                            </Tippy>
                        </div>
                        {showOldWidgetLoader && (
                            <OldWidgetLoadingIndicator
                                color={colors?.horizontalWidgetLoaderColor}
                            />
                        )}
                    </div>
                </div>
            </CSSTransition>

            {showToggleBtn && (
                <ToggleWidgetRoundBtn
                    chatBtnOnClick={chatBtnHandleClick}
                    toggleWidgetRoundBtn={toggleWidgetRoundBtn}
                    toggleWidget={toggleWidget}
                    widgetPosition={widgetPosition}
                    showWidget={showWidget}
                    enableXBtn={enableXBtn}
                    ourPrice={ourPrice}
                    // If this is set to false, the animation might not play correctly
                    // This prop does not work due to 'showToggleBtn'. The button is not
                    // shown at all
                    // showOnlyWhenClosed={true}
                    showCta={showCta}
                />
            )}
        </div>
    );
};

DesktopHorizontalBookingWidget.propTypes = {
    showWidget: PropTypes.func.isRequired,
    toggleWidget: PropTypes.func.isRequired,
};

export default DesktopHorizontalBookingWidget;
